import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import * as XLSX from "xlsx";

function SiblingReport() {
  const storedSession = localStorage.getItem("currentSessionId") || "";
  const [session] = useState(storedSession);

  const [students, setStudents] = useState([]);
  const [siblingFamilies, setSiblingFamilies] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const printRef = useRef();

  // Fetch all students
  useEffect(() => {
    if (!session) return;
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/students/getStudents`, {
        params: { sessionId: session },
      })
      .then((res) => setStudents(res.data))
      .catch((err) => console.error(err));
  }, [session]);

  // Find families with more than one student
useEffect(() => {
  const familyMap = {};

  students.forEach((stu) => {
    const key =
      `${stu.fatherName?.trim().toLowerCase()}-${stu.motherName?.trim().toLowerCase()}`;

    if (!familyMap[key]) familyMap[key] = [];
    familyMap[key].push(stu);
  });

  const siblingsOnly = Object.values(familyMap).filter(
    (family) => family.length > 1
  );

  setSiblingFamilies(siblingsOnly);
  setFilteredStudents(siblingsOnly.flat());
}, [students]);

  // Filter by searchTerm (admission_id or familyId)
useEffect(() => {
  if (!searchTerm) {
    setFilteredStudents(siblingFamilies.flat());
    return;
  }
  const filtered = siblingFamilies
    .flat()
    .filter(
      (s) =>
        s.admission_id.toString().includes(searchTerm) ||
        s.familyId?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  setFilteredStudents(filtered);
}, [searchTerm, siblingFamilies]);

  // Export Excel
  const exportToExcel = () => {
    const data = filteredStudents.map((stu, index) => ({
      "Sr No.": index + 1,
      "Admission No": stu.admission_id,
      Name: `${stu.firstName} ${stu.lastName}`,
      "Father's Name": stu.fatherName,
      "Mother's Name": stu.motherName,
      "Class": stu.class,
      Section: stu.section,
      Gender: stu.gender,
      Category: stu.category,
      Financial: stu.financial,
      Status: stu.status,
      Phone: stu.phoneNo,
    }));
    data.push({
      "Sr No.": "",
      Name: `Total Students with Siblings: ${filteredStudents.length}`,
    });

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sibling Report");
    XLSX.writeFile(workbook, "Sibling_Report.xlsx");
  };

  // Print
  const handlePrint = () => {
    const printContents = printRef.current.innerHTML;
    const newWindow = window.open("", "", "width=900,height=650");
    newWindow.document.write(`
      <html>
        <head>
          <title>Sibling Report</title>
          <style>
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align:center; }
            th { background: #f3f4f6; }
            .badge { padding: 5px 8px; color: white; border-radius: 5px; font-weight:bold; }
            .active { background: green; }
            .inactive { background: red; }
          </style>
        </head>
        <body>
          <h2 style="text-align:center;">Sibling Report</h2>
          <p style="text-align:center; font-weight:bold;">Total Students with Siblings: ${filteredStudents.length}</p>
          ${printContents}
        </body>
      </html>
    `);
    newWindow.document.close();
    newWindow.print();
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-6xl mx-auto bg-white shadow-xl rounded-xl p-8">
        <h2 className="text-2xl font-bold mb-6 text-gray-700">
          Sibling Report
        </h2>

        {/* Search */}
        <div className="mb-4 flex flex-col md:flex-row gap-4 items-center">
          <input
            type="text"
           placeholder="Search by Admission ID"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border rounded px-4 py-2 flex-1"
          />
          <div className="font-semibold text-lg text-yellow-700">
            Total Matched Students: {filteredStudents.length}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 mb-6">
          <button
            onClick={exportToExcel}
            className="bg-green-600 text-white px-4 py-2 rounded shadow hover:bg-green-700"
          >
            Export Excel
          </button>
          <button
            onClick={handlePrint}
            className="bg-blue-600 text-white px-4 py-2 rounded shadow hover:bg-blue-700"
          >
            Print
          </button>
        </div>

        {/* Table */}
        <div ref={printRef} className="overflow-x-auto">
          <table className="min-w-full border text-center">
            <thead className="bg-gray-200">
              <tr>
                <th className="border px-2 py-2">Sr</th>
                <th className="border px-2 py-2">Admission</th>
                <th className="border px-2 py-2">Name</th>
                <th className="border px-2 py-2">Father</th>
                <th className="border px-2 py-2">Mother</th>
                <th className="border px-2 py-2">Class</th>
                <th className="border px-2 py-2">Section</th>
                <th className="border px-2 py-2">Gender</th>
                <th className="border px-2 py-2">Category</th>
                <th className="border px-2 py-2">Financial</th>
                <th className="border px-2 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map((s, i) => (
                <tr key={s._id} className="hover:bg-gray-50">
                  <td className="border px-2 py-2">{i + 1}</td>
                  <td className="border px-2 py-2">{s.admission_id}</td>
                  <td className="border px-2 py-2">{s.firstName} {s.lastName}</td>
                  <td className="border px-2 py-2">{s.fatherName}</td>
                  <td className="border px-2 py-2">{s.motherName}</td>
                  <td className="border px-2 py-2">{s.class}</td>
                  <td className="border px-2 py-2">{s.section}</td>
                  <td className="border px-2 py-2">{s.gender}</td>
                  <td className="border px-2 py-2">{s.category}</td>
                  <td className="border px-2 py-2">{s.financial}</td>
                  <td className="border px-2 py-2">
                    <span
                      className={`px-2 py-1 text-white rounded ${
                        s.status?.toLowerCase() === "active"
                          ? "bg-green-600"
                          : "bg-red-600"
                      }`}
                    >
                      {s.status}
                    </span>
                  </td>
                  
                </tr>
              ))}
              {filteredStudents.length === 0 && (
                <tr>
                  <td colSpan={12} className="border px-2 py-2 text-gray-500">
                    No students with siblings found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default SiblingReport;